/**
 * Cheat console for dev/playtesting.
 * Toggle with backtick (`). Type a command and press Enter.
 */

import { theme } from './theme';
import { isDevMode } from './devMode';

let overlayEl: HTMLDivElement | null = null;
let inputEl: HTMLInputElement | null = null;
let logEl: HTMLDivElement | null = null;
let onCommand: ((cmd: string) => string | null) | null = null;

function ensureDOM(): void {
  if (overlayEl) return;

  overlayEl = document.createElement('div');
  overlayEl.id = 'cheat-console';
  overlayEl.style.cssText = `
    position: fixed; bottom: 0; left: 0; width: 100%; z-index: 9999;
    background: ${theme.surface.overlayDeep}; border-top: 1px solid ${theme.text.disabled};
    font-family: monospace; font-size: 13px; display: none;
  `;

  logEl = document.createElement('div');
  logEl.style.cssText = `
    max-height: 120px; overflow-y: auto; padding: 4px 8px; color: ${theme.text.console};
  `;
  overlayEl.appendChild(logEl);

  const row = document.createElement('div');
  row.style.cssText = 'display:flex; align-items:center; padding:4px 8px;';

  const prompt = document.createElement('span');
  prompt.textContent = '> ';
  prompt.style.color = theme.accent;
  row.appendChild(prompt);

  inputEl = document.createElement('input');
  inputEl.type = 'text';
  inputEl.style.cssText = `
    flex: 1; background: transparent; border: none; outline: none;
    color: ${theme.text.bright}; font-family: monospace; font-size: 13px;
  `;
  inputEl.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      const cmd = inputEl!.value.trim();
      if (cmd && onCommand) {
        appendLog(`> ${cmd}`, theme.text.secondary);
        const result = onCommand(cmd);
        if (result) appendLog(result);
      }
      inputEl!.value = '';
    }
    if (e.key === 'Escape' || e.key === '`') {
      hideCheatConsole();
    }
  });
  row.appendChild(inputEl);
  overlayEl.appendChild(row);
  document.body.appendChild(overlayEl);
}

function appendLog(text: string, color = theme.text.console): void {
  if (!logEl) return;
  const line = document.createElement('div');
  line.textContent = text;
  line.style.color = color;
  logEl.appendChild(line);
  logEl.scrollTop = logEl.scrollHeight;
}

export function setupCheatConsole(handler: (cmd: string) => string | null): void {
  onCommand = handler;
  ensureDOM();
  window.addEventListener('keydown', (e) => {
    if (e.key === '`' && isDevMode()) {
      e.preventDefault();
      toggleCheatConsole();
    }
  });
}

export function toggleCheatConsole(): void {
  if (!overlayEl) return;
  const visible = overlayEl.style.display !== 'none';
  if (visible) {
    hideCheatConsole();
  } else {
    overlayEl.style.display = 'block';
    inputEl?.focus();
  }
}

export function hideCheatConsole(): void {
  if (overlayEl) overlayEl.style.display = 'none';
}

export function isCheatConsoleOpen(): boolean {
  return overlayEl?.style.display !== 'none';
}
